import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, usePrefs } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import Skeleton from 'wsm-skeleton';
import { Image, MediaFocusedCarouselLayout } from 'wsm-vehicle-media-gallery';
import { setClassNames } from 'ddc-classnames-js';
import { useWindowWidth } from 'wsm-srp-utilities';
import CarouselImagePlaceholder from './CarouselImagePlaceholder';
import VideoLinkButton from './VideoLinkButton';
import { getVehicleObj } from '../../../utilities/vehicleTracking';
import { viewportWidthSizes, CAROUSEL_TYPES } from '../../../global-constants';
import { addImageParams } from '../../../utilities/images';
import './CarouselContainer.scss';

// Dynamic imports for both carousel types
const LazyImageCarousel = lazy(() =>
	import(
		/* webpackChunkName: 'new-media-focused-carousel' */ 'wsm-vehicle-media-gallery'
	).then((module) => ({
		default: module.ImageCarousel
	}))
);

const LazyMediaFocusedCarousel = lazy(() =>
	import(
		/* webpackChunkName: 'new-media-focused-carousel' */ 'wsm-vehicle-media-gallery'
	).then((module) => ({
		default: module.MediaFocusedCarousel
	}))
);

const { IMAGE, MEDIA_FOCUSED } = CAROUSEL_TYPES;

// Wrapper component to conditionally apply MediaFocusedCarousel layout
const LayoutWrapper = ({ children, carouselType }) =>
	carouselType === MEDIA_FOCUSED ? (
		<MediaFocusedCarouselLayout>{children}</MediaFocusedCarouselLayout>
	) : (
		children
	);

LayoutWrapper.propTypes = {
	children: PropTypes.node.isRequired,
	carouselType: PropTypes.oneOf([IMAGE, MEDIA_FOCUSED]).isRequired
};

const CarouselContainer = ({
	shouldFullyRenderCard,
	willRenderBlankPlaceholderImage,
	images = [],
	link,
	uuid,
	videos = [],
	title,
	phone,
	usePlaceholderImage
}) => {
	const windowWidth = useWindowWidth();
	const isLargeScreen =
		windowWidth >= viewportWidthSizes.MD_VIEWPORT_WIDTH_SIZE;
	const { widgetName, windowId } = useRequestData();
	const { showSingleImageCarousel, showVideoModalButton } = usePrefs();

	const carouselType =
		isLargeScreen && showSingleImageCarousel !== 'true'
			? MEDIA_FOCUSED
			: IMAGE;

	const vehicleImage = (
		<LayoutWrapper carouselType={carouselType}>
			<Image
				image={images[0]}
				isLoaded={willRenderBlankPlaceholderImage}
				className="w-100"
			/>
		</LayoutWrapper>
	);

	const placeholderImageSrc =
		usePlaceholderImage && images[0]?.uri
			? addImageParams(images[0].uri)
			: null;

	const trackInteraction = (direction) => {
		trackEvent(widgetName, windowId, {
			element: `Photo carousel ${direction} button`,
			result: `Move to ${direction} slide`,
			uuid,
			vehicle: getVehicleObj(uuid)
		});
	};

	const trackImageClick = () => {
		trackEvent(widgetName, windowId, {
			element: 'Vehicle photo clicked in MediaFocusedCarousel',
			result: 'Vehicle clicked, navigated to VDP',
			uuid,
			vehicle: getVehicleObj(uuid)
		});
	};

	// Determine which carousel component to render
	const CarouselComponent =
		carouselType === IMAGE ? LazyImageCarousel : LazyMediaFocusedCarousel;

	let vehicleMedia;

	// First placeholder vehicle: render blank image for LCP hack via ws-inv-data preload code
	if (willRenderBlankPlaceholderImage) {
		vehicleMedia = vehicleImage;
	}
	// Inventory is present: render carousel
	else if (shouldFullyRenderCard && isBrowser) {
		vehicleMedia = (
			<div className="vehicle-media-wrapper">
				<Suspense fallback={vehicleImage}>
					<div className="vehicle-image-skeleton">
						<LayoutWrapper carouselType={carouselType}>
							<Skeleton
								skeletonClasses="embed-responsive"
								placeholderImage={placeholderImageSrc}
							/>
						</LayoutWrapper>
					</div>
					<CarouselComponent
						images={images}
						onNextClick={() => trackInteraction('next')}
						onPrevClick={() => trackInteraction('previous')}
						onImageClick={trackImageClick}
						link={link}
						uuid={uuid}
						CarouselImagePlaceholder={CarouselImagePlaceholder}
					/>
				</Suspense>
				{showVideoModalButton === 'true' && videos.length > 0 && (
					<VideoLinkButton
						widgetName={widgetName}
						windowId={windowId}
						uuid={uuid}
						videos={videos}
						title={title}
						link={link}
						phone={phone}
					/>
				)}
			</div>
		);
	}
	// Placeholder vehicles after the first: render skeleton shimmer
	else {
		vehicleMedia = (
			<Skeleton
				skeletonClasses="mb-4"
				aspectRatio="4by3"
				dataTestId="media-skeleton"
			/>
		);
	}

	return (
		<div
			className={setClassNames([
				'vehicle-card-media-container',
				'list-card-media-container',
				// Only add single-image-media-gallery class for ImageCarousel type
				carouselType === IMAGE && 'single-image-media-gallery',
				'p-3',
				!shouldFullyRenderCard && 'm-0'
			])}
		>
			{vehicleMedia}
		</div>
	);
};

CarouselContainer.propTypes = {
	shouldFullyRenderCard: PropTypes.bool,
	willRenderBlankPlaceholderImage: PropTypes.bool,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			alt: PropTypes.string,
			id: PropTypes.string,
			provider: PropTypes.string,
			title: PropTypes.string,
			uri: PropTypes.string
		})
	),
	link: PropTypes.string,
	uuid: PropTypes.string.isRequired,
	videos: PropTypes.arrayOf(
		PropTypes.shape({
			uri: PropTypes.string,
			provider: PropTypes.string
		})
	),
	title: PropTypes.arrayOf(PropTypes.string),
	phone: PropTypes.string,
	usePlaceholderImage: PropTypes.bool
};

export default CarouselContainer;
